import { isNumber } from 'lodash';
import { ApartmentEntry, PolygonType, EProjectPolygonPriceBy, TProject } from '../../types/config';
import { formatPrice } from '../../utils';
import { InventoryRow, InventoryRowColValue } from './types';

const inventoryRowDefaults = {
  name: '',
  [InventoryRowColValue.Rooms]: 0,
  roomsOutput: '-',
  [InventoryRowColValue.LivingAreas]: 0,
  livingAreasOutput: '-',
  [InventoryRowColValue.Floors]: 0,
  floorsOutput: '-',
  [InventoryRowColValue.Prices]: 0,
  pricesOutput: '-',
};

/**
 * Formats floor display string from minFloor and maxFloor values.
 * Handles ranges, single floors, negative floors (basements), and optional ground floor translation.
 *
 * @param minFloor - Minimum floor number (can be null/undefined)
 * @param maxFloor - Maximum floor number (can be null/undefined)
 * @param t - Optional translation function for "Ground Floor" label. When provided, "0" is translated to "Ground Floor"
 * @returns Formatted floor string
 *          - With translation: "5", "3-7", "Ground Floor", "Ground Floor-5", "-2-Ground Floor"
 *          - Without translation: "5", "3-7", "0", "0-5", "-2-0"
 *          - Returns empty string if no valid floor data
 */
export function getFloorDisplay(
  minFloor: number | null | undefined,
  maxFloor: number | null | undefined,
  t?: (key: string, options?: any) => string,
): string {
  if (!isNumber(minFloor) && !isNumber(maxFloor)) return '';

  const formatFloor = (floor: number) => (t && floor === 0 ? t('labels.ground') : `${floor}`);

  if (isNumber(minFloor) && isNumber(maxFloor)) {
    if (minFloor === maxFloor) return formatFloor(minFloor);
    const [min, max] = [Math.min(minFloor, maxFloor), Math.max(minFloor, maxFloor)];
    return `${formatFloor(min)}-${formatFloor(max)}`;
  }

  return formatFloor((minFloor ?? maxFloor)!);
}


export function getInventoryConfig(
  t: (key: string, options?: any) => string,
  project: TProject,
  aptArray?: ApartmentEntry[],
  isWizardPreview: boolean = false,
): [InventoryRow[], boolean, boolean, boolean, boolean] {
  let withRooms = false;
  let withLivingAreas = false;
  let withFloors = false;
  let withPrices = false;

  const rowsData: InventoryRow[] = project.listings.map((data) => {
    const aptEntry = aptArray?.find((apt) => data.tourUrl && apt.tourUrl === data.tourUrl);
    // search asset only for landing
    const blueprintAsset =
      !isWizardPreview &&
      project.assets.find((a) => a.listingId === data.id || (a as any).polygonId === data.id);

    // Use the same logic as before the migration: check aptEntry, then direct blueprint asset, then processed asset
    const tourPreviewUrl =
      aptEntry?.mainImage || (data as any).blueprintAsset?.preview || blueprintAsset?.url;

    const row: InventoryRow = {
      ...inventoryRowDefaults,
      title: t(`labels.${data.type}`),
      type: data.type,
      polygonId: data.id, // Keep for backward compatibility
      listingId: data.id, // Add listingId
      nrId: aptEntry?.nrId || data.id || 0,
      tourUrl: aptEntry?.tourUrl || data.tourUrl,
      tourPreviewUrl:
        aptEntry?.mainImage || // hardcoded
        data?.blueprintAsset?.preview || // admin
        blueprintAsset?.url, // landing
      order: data.order || 0,
    };
    if (!!data.rooms) {
      row[InventoryRowColValue.Rooms] = Math.abs(data.rooms);
      row.roomsOutput = Math.abs(data.rooms);
      withRooms = true;
    }
    if (!!data.livingArea) {
      row[InventoryRowColValue.LivingAreas] = Math.abs(data.livingArea);
      row.livingAreasOutput = t('labels.m2', { v: Math.abs(data.livingArea) });
      withLivingAreas = true;
    }
    if (isNumber(data.minFloor) || isNumber(data.maxFloor)) {
      const minFloor = data.minFloor;
      const maxFloor = data.maxFloor;

      // Calculate min for sorting value (use 0 as fallback only for comparison)
      const floorsMin = Math.min(minFloor ?? 0, maxFloor ?? 0);
      row[InventoryRowColValue.Floors] = Math.abs(floorsMin);

      // Use the reusable floor display utility
      const floorDisplay = getFloorDisplay(minFloor, maxFloor, t);
      if (floorDisplay) {
        row.floorsOutput = floorDisplay;
        withFloors = true;
      }
    }
    if (data.showPrice && Number(data.price)) {
      row[InventoryRowColValue.Prices] = Math.abs(data.price);
      row.pricesOutput = t(
        data.priceBy === EProjectPolygonPriceBy.Total
          ? 'labels.startingFromMoney'
          : 'labels.startingFromPerSquareMeters',
        { v: formatPrice(Math.abs(data.price)) },
      );
      withPrices = true;
    }
    return row;
  });

  return [rowsData, withRooms, withLivingAreas, withFloors, withPrices];
}
